import type { Patch } from "./types";
import { clonePatch } from "./defaultPatch";

// True parameter morphing between two sounds (vs. the voice crossfade, which
// plays A and B together). `morphPatch(a, b, t)` returns the concrete
// in-between sound at t ∈ 0..1 — a plain portable Patch you can play, save and
// export like any other. Numeric fields interpolate (frequencies on a log
// scale); discrete fields (waveform, filter type, LFO shape/target) switch at
// the halfway point.

const lin = (a: number, b: number, t: number): number => a + (b - a) * t;
const log = (a: number, b: number, t: number): number => a * Math.pow(b / a, t);
const r3 = (v: number): number => Math.round(v * 1000) / 1000;

export function morphPatch(a: Patch, b: Patch, t: number): Patch {
  const m = clonePatch(t < 0.5 ? a : b); // discrete fields from the nearer end
  delete m.xfadeTo;

  m.oscillator.detune = r3(lin(a.oscillator.detune, b.oscillator.detune, t));
  m.oscillator.pulseWidth = r3(lin(a.oscillator.pulseWidth, b.oscillator.pulseWidth, t));
  m.filter.cutoff = Math.round(log(a.filter.cutoff, b.filter.cutoff, t));
  m.filter.q = r3(lin(a.filter.q, b.filter.q, t));
  m.ampEnv.attack = r3(lin(a.ampEnv.attack, b.ampEnv.attack, t));
  m.ampEnv.decay = r3(lin(a.ampEnv.decay, b.ampEnv.decay, t));
  m.ampEnv.sustain = r3(lin(a.ampEnv.sustain, b.ampEnv.sustain, t));
  m.ampEnv.release = r3(lin(a.ampEnv.release, b.ampEnv.release, t));
  m.lfo.rate = r3(log(a.lfo.rate, b.lfo.rate, t));
  m.lfo.depth = r3(lin(a.lfo.depth, b.lfo.depth, t));
  m.masterGain = r3(lin(a.masterGain, b.masterGain, t));
  m.drive = r3(lin(a.drive ?? 0, b.drive ?? 0, t));

  const pa = a.pitchEnv ?? { amount: 0, decay: b.pitchEnv?.decay ?? 0.08 };
  const pb = b.pitchEnv ?? { amount: 0, decay: a.pitchEnv?.decay ?? 0.08 };
  m.pitchEnv = { amount: r3(lin(pa.amount, pb.amount, t)), decay: r3(lin(pa.decay, pb.decay, t)) };

  // A missing FM block borrows the other side's ratio so only depth fades.
  const fa = a.fm ?? { ratio: b.fm?.ratio ?? 1, depth: 0 };
  const fb = b.fm ?? { ratio: a.fm?.ratio ?? 1, depth: 0 };
  m.fm = { ratio: r3(lin(fa.ratio, fb.ratio, t)), depth: r3(lin(fa.depth, fb.depth, t)) };

  const ea = a.filterEnv ?? { amount: 0, decay: b.filterEnv?.decay ?? 0.15 };
  const eb = b.filterEnv ?? { amount: 0, decay: a.filterEnv?.decay ?? 0.15 };
  m.filterEnv = { amount: Math.round(lin(ea.amount, eb.amount, t)), decay: r3(lin(ea.decay, eb.decay, t)) };

  m.name = `${a.name}↔${b.name}`.slice(0, 22);
  return m;
}
